import '@shopify/polaris/build/esm/styles.css'

import React from 'react'

import {
  Button,
  Card,
  Page,
  TextContainer,
} from '@shopify/polaris'

import {
  AUTH_TOKEN_KEY,
  createApi,
} from '../api'
import { getTranslation } from '../util/getTranslation'
import App from './App'
import {
  AuthProvider,
  TAuthContext,
} from './AuthContext'
import { I18nProvider } from './I18nProvider'

/** Backend route that starts the OAuth flow, keeps Shopify's hmac query */
const installUrl = `${window.location.origin}/app${window.location.search}`

export function Auth() {
  const [auth, setAuth] = React.useState<TAuthContext | null>(null)
  const [needsInstall, setNeedsInstall] = React.useState(false)

  React.useEffect(() => {
    const api = createApi()
    api
      .authenticate()
      .then(({ token, account, shopAdminUrl, shopGatewayUrl }) => {
        if (!account) {
          // Missing, uninstalled or reauthorization_required account: the backend
          // cannot redirect a background request, so the OAuth flow starts here.
          if (window.top === window.self) {
            window.location.replace(installUrl)
            return
          }
          // Embedded in the admin: Shopify's consent page refuses to load inside
          // the iframe, so navigate the top window. The admin may allow that only
          // on a user gesture, hence the button as a fallback.
          try {
            window.top!.location.href = installUrl
          } catch {
            // cross-origin navigation blocked, the button below takes over
          }
          setNeedsInstall(true)
          return
        }

        // stored token is used by API calls
        window.sessionStorage.setItem(AUTH_TOKEN_KEY, token)

        setAuth({
          account,
          shopAdminUrl,
          shopGatewayUrl,
          async refreshAccount() {
            const { account } = await api.readAccount()
            if (account === null) {
              // poorly handled, but it shouldn't happen
              window.location.replace(`/error?message=account not found`)
              throw new Error('Account vanished when refreshing, duh?')
            }
            setAuth((cfg) => ({ ...cfg!, account }))
            return account
          },
        })
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') {
          console.error(err)
          return
        }
        window.location.replace(`/error?message=${err.message}`)
      })
  }, [])

  if (needsInstall) {
    return (
      <I18nProvider>
        <Page narrowWidth>
          <Card sectioned>
            <TextContainer>
              <p>
                {getTranslation(
                  'Aplikace potřebuje znovu povolit přístup k vašemu obchodu. Autorizace proběhne v hlavním okně Shopify.',
                  'The app needs to be authorized for your shop again. The authorization opens in the main Shopify window.',
                )}
              </p>
              <Button primary onClick={() => window.open(installUrl, '_top')}>
                {getTranslation('Autorizovat aplikaci', 'Authorize the app')}
              </Button>
            </TextContainer>
          </Card>
        </Page>
      </I18nProvider>
    )
  }

  if (!auth) {
    return null
  }

  return (
    <AuthProvider value={auth}>
      <App />
    </AuthProvider>
  )
}
